/**
 * Thin typed fetch wrapper around the bridge management/wizard API. Always sends cookie credentials so the
 * interactive OIDC session works after first-start. On 401/403 the browser is redirected to the bridge's
 * /api/bridge/wizard/challenge endpoint, mirroring the legacy wizard.js behavior.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

import { normalizeChallengeReturnPath } from "@/lib/challenge";

type FetchOptions = Omit<RequestInit, "body"> & { body?: unknown };

async function request<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, headers, method, ...rest } = options;
  const init: RequestInit = {
    credentials: "include",
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    ...rest,
  };

  const response = await fetch(path, init);

  if (response.status === 401 || response.status === 403) {
    const returnUrl = encodeURIComponent(normalizeChallengeReturnPath());
    window.location.href = `/api/bridge/wizard/challenge?returnUrl=${returnUrl}`;
    throw new ApiError(response.status, "Authentication required.");
  }

  const text = await response.text();
  if (!response.ok) {
    let detail = text;
    try {
      const parsed = JSON.parse(text) as { error?: string; Error?: string; message?: string };
      detail = parsed.error ?? parsed.Error ?? parsed.message ?? text;
    } catch {
      // keep raw text
    }
    throw new ApiError(response.status, detail || `HTTP ${response.status}`, text);
  }

  if (response.status === 204 || text.length === 0) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

import type {
  BridgeStatus,
  WizardApplyRequest,
  WizardCompleteResponse,
  WizardHealth,
  WizardPreview,
  WizardState,
} from "@/types/wizard";

export const wizardApi = {
  getState: () => request<WizardState>("/api/bridge/wizard/state"),
  getStatus: () => request<BridgeStatus>("/api/bridge/status"),
  getHealth: () => request<WizardHealth>("/api/bridge/wizard/health"),
  preview: () => request<WizardPreview>("/api/bridge/wizard/preview", { method: "POST" }),
  apply: (body: WizardApplyRequest) =>
    request<WizardPreview>("/api/bridge/wizard/apply", { method: "POST", body }),
  result: () => request<WizardPreview>("/api/bridge/wizard/result"),
  /**
   * Finalizes the wizard. The bridge writes AllowAnonymous=false into auth-overrides.json and returns a challenge URL
   * for interactive Zitadel sign-in. Should only be invoked from the explicit "Finish" action at the end of the wizard.
   */
  complete: () => request<WizardCompleteResponse>("/api/bridge/wizard/complete", { method: "POST" }),
};
